/**
 * Turning server errors into something a host can read.
 *
 * Two sources:
 *   1. Edge Functions (supabase.functions.invoke). Business errors arrive as
 *      HTTP 200 { error_code, error }. Real failures (4xx/5xx) arrive as a
 *      FunctionsHttpError whose `context` is the fetch Response — its JSON
 *      body usually still carries { error_code?, error }. We never show the
 *      library's "Edge Function returned a non-2xx status code".
 *   2. Database errors (PostgREST / RPC / triggers). The billing triggers
 *      raise messages that start with a code (PLAN_REQUIRED, UNIT_INACTIVE,
 *      HAS_STRIPE_SUBSCRIPTION, UNIT/PROPERTY_LIMIT_REACHED, "Unit limit
 *      reached. Current: x, Max: y").
 */

export interface FunctionErrorInfo {
  /** Machine code from the body (error_code), or a transport code. */
  code: string | null;
  /** Server text from the body (error / message) — English, for logs. */
  message: string | null;
  /** HTTP status when the call failed at the transport level. */
  status: number | null;
  /** Whole parsed body (extra fields like active_units / max_units). */
  body: Record<string, unknown> | null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Read the error out of a functions.invoke result. Returns null when the call
 * succeeded without a business error.
 */
export async function readFunctionError(
  data: unknown,
  error: unknown,
): Promise<FunctionErrorInfo | null> {
  if (error) {
    let body: Record<string, unknown> | null = null;
    let status: number | null = null;
    const ctx = (error as { context?: unknown }).context;
    if (typeof Response !== 'undefined' && ctx instanceof Response) {
      status = ctx.status;
      try {
        const parsed = await ctx.clone().json();
        if (isRecord(parsed)) body = parsed;
      } catch {
        // not JSON
      }
    } else if (isRecord(ctx) && typeof ctx.status === 'number') {
      status = ctx.status;
    }
    const name = (error as { name?: string }).name;
    const transport =
      name === 'FunctionsFetchError' ? 'NETWORK'
      : name === 'FunctionsRelayError' ? 'RELAY'
      : status === 401 || status === 403 ? 'SESSION'
      : status === 429 ? 'RATE_LIMITED'
      : 'SERVER';
    return {
      code: (body && typeof body.error_code === 'string' ? body.error_code : null) ?? transport,
      message: body && typeof body.error === 'string' ? body.error : null,
      status,
      body,
    };
  }
  if (isRecord(data) && (typeof data.error_code === 'string' || typeof data.error === 'string')) {
    return {
      code: typeof data.error_code === 'string' ? data.error_code : 'SERVER',
      message: typeof data.error === 'string' ? data.error : null,
      status: 200,
      body: data,
    };
  }
  return null;
}

/** An Error carrying the function error info (thrown by hooks). */
export class BillingError extends Error {
  code: string;
  info: FunctionErrorInfo | null;
  constructor(code: string, info: FunctionErrorInfo | null = null, message?: string) {
    super(message ?? info?.message ?? code);
    this.name = 'BillingError';
    this.code = code;
    this.info = info;
  }
}

export type DbBillingCode =
  | 'PLAN_REQUIRED'
  | 'UNIT_INACTIVE'
  | 'HAS_STRIPE_SUBSCRIPTION'
  | 'PERIOD_ENDED_USE_GIFT'
  | 'PLAN_REQUIRED_FOR_ACTIVE'
  | 'UNIT_LIMIT'
  | 'PROPERTY_LIMIT';

/** Every text field a Postgres / PostgREST error may carry, joined. */
function errorText(err: unknown): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  const e = err as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown };
  return [e.message, e.details, e.hint, e.code].filter((x) => typeof x === 'string').join(' | ');
}

/** Detect the billing codes the triggers / admin RPCs raise. */
export function dbBillingCode(err: unknown): DbBillingCode | null {
  const text = errorText(err);
  if (!text) return null;
  // (before PLAN_REQUIRED — it contains it)
  if (/PLAN_REQUIRED_FOR_ACTIVE/.test(text)) return 'PLAN_REQUIRED_FOR_ACTIVE';
  if (/PLAN_REQUIRED/.test(text)) return 'PLAN_REQUIRED';
  if (/UNIT_INACTIVE/.test(text)) return 'UNIT_INACTIVE';
  if (/HAS_STRIPE_SUBSCRIPTION/.test(text)) return 'HAS_STRIPE_SUBSCRIPTION';
  if (/PERIOD_ENDED_USE_GIFT/.test(text)) return 'PERIOD_ENDED_USE_GIFT';
  if (/UNIT_LIMIT_REACHED|unit limit reached/i.test(text)) return 'UNIT_LIMIT';
  if (/PROPERTY_LIMIT_REACHED|property limit reached/i.test(text)) return 'PROPERTY_LIMIT';
  return null;
}

/** Message text of any thrown value (for LimitErrorAlert-style parsers). */
export function errorMessageOf(err: unknown): string {
  return errorText(err) || (err instanceof Error ? err.message : String(err ?? ''));
}
